import {Injectable} from '@angular/core';
import {Landlord} from '@landlords/models';
import {LandlordsService} from '@landlords/services/landlords.service';
import {resolveTaxProfile} from '@vat/engine/tax-profile';
import type {LandlordTaxProfile, VatSuggestionDismissed} from '@vat/engine/types';
import firebase from 'firebase/compat/app';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {normalizeUndefinedToNull} from './vat-return.factory';

/**
 * Lese-/Schreibzugriff auf `Landlord.taxProfile` (Spec 3.1). Einziger Schreibpfad ist `save()`:
 * `landlordsService.doc(id).update({taxProfile})` mit `new Date()` und explizitem `null` –
 * kein removeEmpty, kein serverTimestamp, keine weiteren Landlord-Felder.
 *
 * `resolveTaxProfile` braucht einen Stichtag für den aktuellen `besteuerung`-Eintrag (Plan 1);
 * die Lese-Methoden nehmen ihn optional entgegen und fallen auf „heute“ zurück.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
@Injectable({providedIn: 'root'})
export class VatTaxProfileService {

    constructor(private landlordsService: LandlordsService) {
    }

    landlord$(landlordID: string): Observable<Landlord> {
        return this.landlordsService.doc(landlordID).valueChanges({idField: 'id'});
    }

    /** Vollständig aufgelöstes Profil; ohne gespeichertes Profil STEUERFREI/SOLL mit `setupCompletedAt: null`. */
    taxProfile$(landlordID: string, referenceDate: Date = new Date()): Observable<LandlordTaxProfile> {
        return this.landlord$(landlordID).pipe(map(landlord => resolveTaxProfile(landlord?.taxProfile, referenceDate)));
    }

    /** Nur `setupCompletedAt === null` öffnet das Setup-Gate (Spec 3.1). */
    isSetupOpen(landlord: Landlord | null | undefined, referenceDate: Date = new Date()): boolean {
        return resolveTaxProfile(landlord?.taxProfile, referenceDate).setupCompletedAt === null;
    }

    save(landlordID: string, taxProfile: LandlordTaxProfile): Promise<void> {
        return this.landlordsService.doc(landlordID).update({taxProfile: normalizeUndefinedToNull(taxProfile)});
    }

    /**
     * Landing-Vorschlag „noch kein Steuerprofil“ für das ganze Team ausblenden (Landing v2, Mock-Freigabe 13.09.):
     * dotted key `taxProfile.suggestionDismissed` NUR mit `update()` – `set(merge)` würde einen literalen Feldnamen anlegen.
     * Schreibt sonst nichts am Steuerprofil; `resolveTaxProfile` ignoriert das Feld, der Eigentümer bleibt „Setup offen“.
     */
    dismissSuggestion(landlordID: string, uid: string, byName: string | null | undefined): Promise<void> {
        const marker: VatSuggestionDismissed = {at: new Date(), by: uid, byName: byName ?? null};
        const patch: Record<string, unknown> = {'taxProfile.suggestionDismissed': marker};
        return this.landlordsService.doc(landlordID).update(patch as Partial<Landlord>);
    }

    /** „Wieder anzeigen“: Marker per `FieldValue.delete()` am dotted key entfernen – das Steuerprofil bleibt unangetastet. */
    restoreSuggestion(landlordID: string): Promise<void> {
        const patch: Record<string, unknown> = {'taxProfile.suggestionDismissed': firebase.firestore.FieldValue.delete()};
        return this.landlordsService.doc(landlordID).update(patch as Partial<Landlord>);
    }
}
